import React, { FC } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, OrderApi } from "../../../../../../../../services/Api/Organization/types";
import { getInvoiceDisabledInputsClasses } from "../../utils";

interface Props {
	form: UseFormReturn<InvoiceApi>;
	invoice: InvoiceApi;
	order?: OrderApi;
	disableInputs: boolean;
}

const InvoiceFormBasicInfo: FC<Props> = ({ form, invoice, order, disableInputs }) => {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		control,
		setValue,
		watch,
	} = form;

	const fetchCorrectionReasons = async (params: Record<string, any>, options?: Record<string, any>) => {
		const res = await apiOrganization.getCorrectionReasons(params, {
			cancelToken: options?.token,
		});
		return res.map((item) => {
			const translation = t(`enums.invoices.reason_of_correction.${item.label}`);
			return { ...item, label: translation.includes(".") ? item.label : translation };
		});
	};

	const handleCreateCorrectionReason = (option: string) => {
		setValue("reason_of_correction", option);
	};

	return (
		<fieldset className="form-group">
			<h5>{t("common.word.info_basic")}</h5>
			<div className={"row"}>
				<div className="col-md-6">
					<FormInput
						label={t("modules.invoice.field.place_of_issue.title")}
						register={register}
						name="place_of_issue"
						errors={errors}
						disabled={disableInputs}
						className={getInvoiceDisabledInputsClasses(disableInputs)}
					/>
				</div>
				<div className="col-md-6">
					<FormDatePicker
						label={t("modules.invoice.field.issued_at.title")}
						control={control}
						name="dates.issued_at"
						timePicker={false}
						errors={errors}
						disabled={disableInputs}
						className={getInvoiceDisabledInputsClasses(disableInputs)}
					/>
				</div>
				{!order && (
					<>
						{(invoice.document_type !== "ADVANCE" || invoice.advance_id) && (
							<div className="col-md-6">
								<FormDatePicker
									label={t("modules.invoice.field.sold_at.title")}
									control={control}
									name="dates.sold_at"
									timePicker={false}
									errors={errors}
									disabled={disableInputs}
									className={getInvoiceDisabledInputsClasses(disableInputs)}
								/>
							</div>
						)}

						<div className="col-md-6">
							<FormDatePicker
								label={t("modules.invoice.field.pay_due_date.title")}
								control={control}
								name="dates.payment_due_date"
								timePicker={false}
								errors={errors}
								disabled={disableInputs}
								className={getInvoiceDisabledInputsClasses(disableInputs)}
							/>
						</div>
					</>
				)}
				<div className="col-md-6">
					<FormInput
						label={t("modules.invoice.field.issuer.title")}
						register={register}
						name="signed_by"
						errors={errors}
						disabled={disableInputs}
						className={getInvoiceDisabledInputsClasses(disableInputs)}
					/>
				</div>
				{invoice.document_type === "CORRECTION" && (
					<div className={"col-md-6"}>
						<FormSelectGroup
							label={t("modules.invoice.field.reason_of_correction.title")}
							name="reason_of_correction"
							defaultValue={{
								id: watch("reason_of_correction"),
								label: t(
									`enums.invoices.reason_of_correction.${watch("reason_of_correction")}`
								).includes(".")
									? watch("reason_of_correction")
									: t(`enums.invoices.reason_of_correction.${watch("reason_of_correction")}`),
							}}
							errors={errors}
							loadOptions={(_: string, params: Record<string, any>, options?: Record<string, any>) =>
								fetchCorrectionReasons(params, options)
							}
							control={control}
							data-testid="reason_of_correction"
							getOptionValue={(option) => option.id}
							forceCreateOption
							onCreateOption={handleCreateCorrectionReason}
							disabled={disableInputs}
							className={getInvoiceDisabledInputsClasses(disableInputs)}
						/>
					</div>
				)}
			</div>
		</fieldset>
	);
};
export default InvoiceFormBasicInfo;

// t("enums.invoices.reason_of_correction.USE_DISCOUNT")
// t("enums.invoices.reason_of_correction.RETURN_PAYMENT")
// t("enums.invoices.reason_of_correction.PRICE_INCREASE")
// t("enums.invoices.reason_of_correction.RETURN_GOOD")
// t("enums.invoices.reason_of_correction.MISTAKE")
// t("enums.invoices.reason_of_correction.CHANGE_RECIPIENT")
